import {Alert, Box, Button, Stack} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import type {
    AcademyCourseStatisticsDTO,
    AcademyDepartmentStatisticsDTO,
} from "crqlar-react-common/dist/models/aggregation/academyStatistics.model";
import {formatDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {InsightsCard} from "../../insights/kit/InsightsCard";
import {InsightsTable, type InsightsColumn} from "../../insights/kit/InsightsTable";
import {insightsLoadingOf} from "../../insights/kit/insightsLoading";
import {StatGrid} from "../../insights/kit/StatGrid";
import type {StatCardData} from "../../insights/kit/StatCard";
import {
    RightSidebarRailHeader,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";
import RangeDatePicker from "../../common/newDatePicker/RangeDatePicker";
import {useDebouncedDateRange} from "../../common/newDatePicker/datePickerShared";
import {useAcademyStatistics} from "./useAcademyStatistics";
import {AcademyStatisticsService} from "crqlar-react-common/dist/services/aggregation/academyStatistics.service";
import {downloadBlobAsFile} from "../../surveys/shared/surveyExport.util";
import {
    PARTICIPATION_COLUMNS,
    participationCsv,
    participationFileName,
} from "./academyParticipationCsv.util";
import {statisticsRange} from "./academyStatisticsRange.util";

/**
 * Akademie participation: how many took part, in what, and from which department.
 *
 * **It is the list tab, counted.** The API reads the same reservations this sector's list shows for
 * the same range, so a figure here can always be traced to rows next door. Nothing is stored and
 * nothing is cached on a profile, which is what stops a total and the list behind it disagreeing.
 *
 * **Pending stands on its own.** A request a head has not decided is a real claim on a seat, so it
 * gets its own column rather than being folded into either the approved or the attended figure. It
 * can never reach the attendance column: attendance is a stamp written by the confirm route on an
 * approved booking.
 *
 * **Attended is part of approved, not beside it.** Five approved of which four attended is five
 * seats given out, not nine. Adding the two columns together is the one mistake this screen can
 * invite, so the attendance card states the pair as a rate rather than leaving it to be worked out.
 */
export default function AcademyStatisticsTab({sectorId}: { sectorId: string }) {

    const {t} = useTranslation();
    // Keyed on the sector, so the range chosen here and the one on the list tab are the same range.
    const {range, queryRange, onRangeChange} = useDebouncedDateRange(sectorId);
    const query = useAcademyStatistics(sectorId, queryRange);

    const data = query.data;
    const loading = insightsLoadingOf(query.isLoading, query.isFetching);
    const totals = data?.totals;

    const percent = (part: number, whole: number) => whole === 0 ? "—" : `${Math.round((part / whole) * 100)}%`;

    const [exporting, setExporting] = useState(false);
    const [exportFailed, setExportFailed] = useState(false);

    /**
     * The bookings behind these figures, as a file HR can work in.
     *
     * Fetched on the click rather than held alongside the figures: it is a list of named people
     * and nobody needs it in memory for simply looking at the screen. It asks for the range that
     * is on screen, through the same widening the figures use, so the file and the tab above it
     * are the same bookings.
     */
    const exportParticipation = async () => {
        setExporting(true);
        setExportFailed(false);
        try {
            const exported = statisticsRange(range);
            const {rows} = await AcademyStatisticsService.participation({sectorId, range: exported});
            const csv = participationCsv(rows, {
                // Translated here rather than in the builder: the file is read by whoever asked
                // for it, in the language the rest of their screen is in.
                columns: PARTICIPATION_COLUMNS.map(column => t(column)),
                yes: t("yes"),
                no: t("no"),
            });
            downloadBlobAsFile(
                // The BOM makes Excel read the file as UTF-8, so "Küche" does not arrive as "KÃ¼che".
                new Blob([`\ufeff${csv}`], {type: "text/csv;charset=utf-8"}),
                participationFileName(exported.start, exported.end),
            );
        } catch (e) {
            setExportFailed(true);
        } finally {
            setExporting(false);
        }
    };

    const cards = useMemo<StatCardData[]>(() => totals ? [
        {
            id: "participants",
            title: t("Participants"),
            hint: t("Employees holding a seat. Someone on two courses is counted once."),
            value: String(totals.participants),
        },
        {
            id: "courses",
            title: t("Courses booked"),
            hint: t("Courses with at least one booking in this period, not the whole catalogue."),
            value: String(totals.courses),
        },
        {
            id: "attendance",
            title: t("Attendance"),
            // The pair, not the percentage alone: 100% of two seats and 100% of two hundred are
            // the same number and a very different afternoon.
            hint: t("Confirmed attendance against the seats a head approved."),
            value: `${totals.attended} / ${totals.approved}  ·  ${percent(totals.attended, totals.approved)}`,
        },
        {
            id: "pending",
            title: t("Waiting for a decision"),
            hint: t("Requests no head has approved or declined yet."),
            value: String(totals.pending),
        },
        {
            id: "credits",
            title: t("Credits awarded"),
            hint: t("What confirmed attendance actually paid, summed from the bookings."),
            value: String(totals.creditsAwarded),
        },
    ] : [], [totals, t]);

    const courseColumns = useMemo<InsightsColumn<AcademyCourseStatisticsDTO>[]>(() => [
        {key: "course", label: t("Course"), kind: "label", cell: row => row.courseName ?? "—"},
        {
            key: "when",
            label: t("First session"),
            kind: "text",
            cell: row => row.firstStart ? formatDateTime("dd.MM.yyyy", new Date(row.firstStart)) : t("No date yet"),
        },
        {key: "participants", label: t("Participants"), kind: "value", cell: row => row.participants},
        {key: "pending", label: t("Pending"), kind: "detail", cell: row => row.pending},
        {key: "approved", label: t("Approved"), kind: "detail", cell: row => row.approved},
        {key: "attended", label: t("Attended"), kind: "value", cell: row => row.attended},
        {key: "cancelled", label: t("Cancelled"), kind: "detail", cell: row => row.cancelled},
        {key: "credits", label: t("Credits"), kind: "detail", cell: row => row.creditsAwarded},
    ], [t]);

    const departmentColumns = useMemo<InsightsColumn<AcademyDepartmentStatisticsDTO>[]>(() => [
        // A booking whose profile carries no department is named here rather than by the API: what
        // to call it is wording, and an employee with no department is a records problem HR can
        // see and fix.
        {key: "department", label: t("Department"), kind: "label", cell: row => row.department ?? t("No department")},
        {key: "employees", label: t("Employees"), kind: "value", cell: row => row.employees},
        {key: "pending", label: t("Pending"), kind: "detail", cell: row => row.pending},
        {key: "approved", label: t("Approved"), kind: "detail", cell: row => row.approved},
        {key: "attended", label: t("Attended"), kind: "value", cell: row => row.attended},
        {key: "cancelled", label: t("Cancelled"), kind: "detail", cell: row => row.cancelled},
        {key: "credits", label: t("Credits"), kind: "detail", cell: row => row.creditsAwarded},
    ], [t]);

    const empty = (text: string) => (
        <Box sx={{py: 4, textAlign: "center", color: "text.secondary", fontSize: 14}}>{text}</Box>
    );

    return (
        /*
            PageShell owns the viewport height and never scrolls itself, so a tab that does not
            bound and scroll its own content simply loses whatever does not fit. The date range
            stays in the pinned header, as on the insights tabs, so it is still reachable after
            scrolling down to the department table.
        */
        <RightSidebarRailLayout>

            <RightSidebarRailHeader>
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap"}}>
                    <RangeDatePicker
                        value={range}
                        onChange={onRangeChange}
                        showStepButtons
                        size={"medium"}
                    />
                    <Button
                        variant={"outlined"}
                        startIcon={<FileDownloadOutlinedIcon/>}
                        onClick={exportParticipation}
                        disabled={exporting || !data}
                    >
                        {exporting ? t("Preparing") : t("Export participation")}
                    </Button>
                </Box>
            </RightSidebarRailHeader>

            <Stack
                sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflowY: "auto",
                    px: 2.5,
                    pt: 0.5,
                    pb: 5,
                    gap: 2,
                    /*
                        InsightsCard fills its column by design, so that two cards side by side in
                        a grid end level with each other. Stacked one above the other in a single
                        column that rule stretches a four-row table over the whole viewport, so
                        here each card is as tall as what it holds.
                    */
                    "& > section": {height: "auto"},
                }}
            >

                {query.isError && (
                    <Alert severity={"error"}>{t("The participation figures could not be loaded")}</Alert>
                )}

                {exportFailed && (
                    <Alert severity={"error"} onClose={() => setExportFailed(false)}>
                        {t("The export could not be prepared")}
                    </Alert>
                )}

                <StatGrid items={cards} loading={loading}/>

                <InsightsCard
                    title={t("Participation per course")}
                    hint={t("One row per course with a booking in this period. Attended is part of approved, not extra to it.")}
                    loading={loading}
                    skeletonHeight={220}
                >
                    {data && data.courses.length > 0
                        ? (
                            <InsightsTable
                                label={t("Participation per course")}
                                columns={courseColumns}
                                rows={data.courses}
                                rowKey={row => row.serviceId}
                            />
                        )
                        : empty(t("No Akademie bookings in this period"))}
                </InsightsCard>

                <InsightsCard
                    title={t("Participation per department")}
                    hint={t("The department on the employee's own profile, the same one their requests are routed by.")}
                    loading={loading}
                    skeletonHeight={220}
                >
                    {data && data.departments.length > 0
                        ? (
                            <InsightsTable
                                label={t("Participation per department")}
                                columns={departmentColumns}
                                rows={data.departments}
                                rowKey={row => row.department ?? "none"}
                            />
                        )
                        : empty(t("No Akademie bookings in this period"))}
                </InsightsCard>

            </Stack>

        </RightSidebarRailLayout>
    );
}
